// ===========================================
// IMPORTS (CPG Hub — inbound shipments & lots)
// ===========================================
// The sea-freight ledger: each shipment, the lots it landed, and what every
// unit truly cost once freight, tariff and distribution are on it. Everything
// here is typed in on this page (or loaded through the hub API) — landed cost
// is computed from those entries, never stored.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hubApi, restaurantApi } from '../services/api';
import EntityToggle, { EntityView } from '../components/EntityToggle';
import { Plus, X, ChevronDown, Pencil, Trash2, Ship, Leaf, TrendingUp, Download, ExternalLink } from 'lucide-react';
import clsx from 'clsx';

const fmt = (c: number) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const toCents = (dollars: string) => Math.round((parseFloat(dollars) || 0) * 100);

type Tab = 'shipments' | 'costs' | 'organic';

const STATUS_LABEL: Record<string, string> = { in_transit: 'In transit', arrived: 'Arrived', cleared: 'Cleared' };
const statusChip = (s: string) =>
  clsx('px-2 py-0.5 rounded-full text-xs font-medium shrink-0', {
    'bg-blue-100 text-blue-700': s === 'in_transit',
    'bg-amber-100 text-amber-700': s === 'arrived',
    'bg-emerald-100 text-emerald-700': s === 'cleared',
  });

const emptyLine = { productName: '', sku: '', quantity: '', unit: 'unit', unitCost: '' };
const emptyImport = { supplierName: '', reference: '', shipDate: '', eta: '', freight: '', tariffPercent: '', distributionPercent: '', notes: '', entity: 'us' };

export default function ImportsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('shipments');
  // US / EU / Combined — which entity's books these shipments land in.
  const [entity, setEntity] = useState<EntityView>('all');
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const say = (tone: 'success' | 'error', text: string) => setBanner({ tone, text });
  const oops = (e: any, fallback: string) => say('error', e?.response?.data?.error?.message || fallback);

  // ---- data
  const { data: importsRes, isPending } = useQuery({
    queryKey: ['hub-imports', entity],
    queryFn: async () => (await hubApi.getImports(entity === 'all' ? undefined : entity)).data,
  });
  const importsList: any[] = importsRes?.data || [];
  const defaults = importsRes?.defaults || { tariffPercent: 10, distributionPercent: 3 };

  const { data: expiring = [] } = useQuery({
    queryKey: ['hub-expiring'],
    queryFn: async () => (await hubApi.getExpiringLots(60)).data?.data || [],
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['hub-imports'] });
    qc.invalidateQueries({ queryKey: ['hub-expiring'] });
    qc.invalidateQueries({ queryKey: ['hub-cost-trend'] });
    qc.invalidateQueries({ queryKey: ['hub-mass-balance'] });
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><Ship className="w-6 h-6 text-primary-600" /> Imports</h1>
          <p className="text-sm md:text-base text-slate-500">Inbound shipments, the lots they land, and what each unit truly costs — all from what you record here.</p>
        </div>
        <EntityToggle value={entity} onChange={setEntity} />
      </div>

      {banner && (
        <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
          banner.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
          <p>{banner.text}</p>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Expiry watch — oldest first, the FIFO nudge */}
      {expiring.length > 0 && (
        <div className="card border-amber-200 bg-amber-50 p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-900">
            {expiring.length} lot{expiring.length === 1 ? '' : 's'} inside the 60-day expiry window — oldest first, sell these before newer stock.
          </p>
          <div className="space-y-1">
            {expiring.slice(0, 6).map((l: any) => (
              <p key={l.id} className="text-sm text-amber-800">
                <span className="font-medium">Lot {l.lotNumber}</span>
                {l.productName ? ` · ${l.productName}` : ''} —{' '}
                {l.daysLeft != null && l.daysLeft <= 0
                  ? <span className="font-semibold text-red-700">expired {fmtDate(l.expirationDate)}</span>
                  : <>expires {fmtDate(l.expirationDate)} ({l.daysLeft} day{l.daysLeft === 1 ? '' : 's'})</>}
                {' '}· {parseFloat(l.quantityRemaining)} {l.unit} left{l.location ? ` at ${l.location}` : ''}
              </p>
            ))}
            {expiring.length > 6 && <p className="text-xs text-amber-700">…and {expiring.length - 6} more below, inside each shipment.</p>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {([['shipments', 'Shipments'], ['costs', 'Landed cost'], ['organic', 'Organic balance']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium shrink-0',
              tab === id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'shipments' && (
        <ShipmentsTab importsList={importsList} isPending={isPending} defaults={defaults} say={say} oops={oops} refresh={refresh} />
      )}
      {tab === 'costs' && <CostsTab defaults={defaults} />}
      {tab === 'organic' && <OrganicTab say={say} oops={oops} refresh={refresh} />}
    </div>
  );
}

// ===========================================
// SHIPMENTS
// ===========================================
function ShipmentsTab({ importsList, isPending, defaults, say, oops, refresh }: {
  importsList: any[]; isPending: boolean; defaults: any;
  say: (tone: 'success' | 'error', text: string) => void; oops: (e: any, fallback: string) => void; refresh: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyImport });
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [openId, setOpenId] = useState<string | null>(null);

  const createImport = useMutation({
    mutationFn: () => hubApi.createImport({
      supplierName: form.supplierName.trim(),
      reference: form.reference.trim() || undefined,
      shipDate: form.shipDate || undefined,
      eta: form.eta || undefined,
      freightCents: toCents(form.freight),
      tariffPercent: form.tariffPercent === '' ? null : parseFloat(form.tariffPercent),
      distributionPercent: form.distributionPercent === '' ? null : parseFloat(form.distributionPercent),
      entity: form.entity === 'eu' ? 'eu' : 'us',
      notes: form.notes.trim() || undefined,
      lines: lines
        .filter((l) => l.productName.trim() && parseFloat(l.quantity) > 0)
        .map((l) => ({
          productName: l.productName.trim(), sku: l.sku.trim() || undefined,
          quantity: parseFloat(l.quantity), unit: l.unit.trim() || 'unit',
          unitCostCents: toCents(l.unitCost),
        })),
    }),
    onSuccess: () => {
      refresh(); setShowForm(false); setForm({ ...emptyImport }); setLines([{ ...emptyLine }]);
      say('success', 'Shipment recorded. Add its lots when the goods are on the shelf.');
    },
    onError: (e: any) => oops(e, "Couldn't record the shipment."),
  });

  return (
    <div className="space-y-4">
      <CostAssumptions defaults={defaults} say={say} oops={oops} refresh={refresh} />

      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> New shipment
        </button>
      ) : (
        <div className="card p-4 space-y-3">
          <p className="font-medium text-slate-900">New shipment</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.supplierName} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} placeholder="Supplier *" className="input" />
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Reference (container / booking no.)" className="input" />
            <label className="text-sm text-slate-600">Ship date
              <input type="date" value={form.shipDate} onChange={(e) => setForm({ ...form, shipDate: e.target.value })} className="input mt-1" />
            </label>
            <label className="text-sm text-slate-600">ETA
              <input type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="input mt-1" />
            </label>
            <label className="text-sm text-slate-600">Freight for the whole shipment ($)
              <input type="number" min="0" step="0.01" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} placeholder="0.00" className="input mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-slate-600">Tariff %
                <input type="number" min="0" step="0.1" value={form.tariffPercent} onChange={(e) => setForm({ ...form, tariffPercent: e.target.value })} placeholder={String(defaults.tariffPercent)} className="input mt-1" />
              </label>
              <label className="text-sm text-slate-600">Distribution %
                <input type="number" min="0" step="0.1" value={form.distributionPercent} onChange={(e) => setForm({ ...form, distributionPercent: e.target.value })} placeholder={String(defaults.distributionPercent)} className="input mt-1" />
              </label>
            </div>
            <label className="text-sm text-slate-600">Entity — whose books this shipment lands in
              <select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} className="input mt-1">
                <option value="us">US</option>
                <option value="eu">EU</option>
              </select>
            </label>
          </div>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (vessel, broker, anything worth remembering)" className="input w-full min-h-[52px]" />

          <p className="text-xs font-medium text-slate-500">Lines — what's on the commercial invoice. Names that match your catalog link automatically (that's what powers the organic sheet).</p>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                <input value={l.productName} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, productName: e.target.value } : x))} placeholder="Product (e.g. Organic EVOO 500ml)" className="input" />
                <input value={l.sku} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, sku: e.target.value } : x))} placeholder="SKU" className="input" />
                <input type="number" min="0" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} placeholder="Qty" className="input" />
                <input value={l.unit} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} placeholder="Unit" className="input" />
                <input type="number" min="0" step="0.01" value={l.unitCost} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unitCost: e.target.value } : x))} placeholder="Cost/unit $" className="input" />
                <button onClick={() => setLines(lines.length > 1 ? lines.filter((_, j) => j !== i) : [{ ...emptyLine }])} className="p-2 text-slate-400 hover:text-red-500" title="Remove line"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setLines([...lines, { ...emptyLine }])} className="btn btn-ghost text-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Add line</button>

          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => createImport.mutate()} className="btn btn-primary" disabled={!form.supplierName.trim() || createImport.isPending}>
              {createImport.isPending ? 'Saving…' : 'Save shipment'}
            </button>
          </div>
        </div>
      )}

      {isPending ? (
        <div className="card p-8 text-center text-slate-400">Loading shipments…</div>
      ) : importsList.length === 0 ? (
        <div className="card p-10 text-center text-slate-500">
          No shipments yet. Record your first import above — lots, expiry dates and landed cost all build from it.
        </div>
      ) : (
        <div className="space-y-2">
          {importsList.map((imp: any) => (
            <ImportCard key={imp.id} imp={imp} open={openId === imp.id} onToggle={() => setOpenId(openId === imp.id ? null : imp.id)}
              defaults={defaults} say={say} oops={oops} refresh={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function CostAssumptions({ defaults, say, oops, refresh }: {
  defaults: any; say: (tone: 'success' | 'error', text: string) => void; oops: (e: any, fallback: string) => void; refresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [tariff, setTariff] = useState('');
  const [dist, setDist] = useState('');
  const save = useMutation({
    mutationFn: () => restaurantApi.updateSettings({
      importCosts: {
        tariffPercent: tariff === '' ? defaults.tariffPercent : parseFloat(tariff),
        distributionPercent: dist === '' ? defaults.distributionPercent : parseFloat(dist),
      },
    }),
    onSuccess: () => { setEditing(false); refresh(); say('success', 'Cost assumptions saved — they apply to every shipment that doesn’t set its own.'); },
    onError: (e: any) => oops(e, "Couldn't save the cost assumptions."),
  });
  return (
    <div className="card p-4">
      {!editing ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">
            Cost assumptions: <span className="font-semibold text-slate-900">tariff {defaults.tariffPercent}%</span> ·{' '}
            <span className="font-semibold text-slate-900">distribution {defaults.distributionPercent}%</span>
            <span className="text-slate-400"> — used when a shipment doesn't set its own.</span>
          </p>
          <button onClick={() => { setTariff(String(defaults.tariffPercent)); setDist(String(defaults.distributionPercent)); setEditing(true); }}
            className="btn btn-ghost text-sm flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-slate-600">Tariff %
            <input type="number" min="0" step="0.1" value={tariff} onChange={(e) => setTariff(e.target.value)} className="input mt-1 w-28" />
          </label>
          <label className="text-sm text-slate-600">Distribution %
            <input type="number" min="0" step="0.1" value={dist} onChange={(e) => setDist(e.target.value)} className="input mt-1 w-28" />
          </label>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending} className="btn btn-primary">{save.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportCard({ imp, open, onToggle, defaults, say, oops, refresh }: {
  imp: any; open: boolean; onToggle: () => void; defaults: any;
  say: (tone: 'success' | 'error', text: string) => void; oops: (e: any, fallback: string) => void; refresh: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editHeader, setEditHeader] = useState(false);
  const [hf, setHf] = useState<any>(null);
  const [lotFormFor, setLotFormFor] = useState<string | null>(null);
  const [lotForm, setLotForm] = useState({ lotNumber: '', productionDate: '', expirationDate: '', location: '', quantityRemaining: '' });
  const [docForm, setDocForm] = useState({ name: '', url: '' });
  const [newLine, setNewLine] = useState<any>(null);

  const invoiceTotal = (imp.lines || []).reduce((s: number, l: any) => s + (l.cost?.invoiceCents || 0), 0);
  const deliveredTotal = (imp.lines || []).reduce((s: number, l: any) => s + (l.cost?.deliveredCents || 0), 0);

  const update = useMutation({
    mutationFn: (data: Record<string, unknown>) => hubApi.updateImport(imp.id, data),
    onSuccess: () => { refresh(); setEditHeader(false); },
    onError: (e: any) => oops(e, "Couldn't update the shipment."),
  });
  const remove = useMutation({
    mutationFn: () => hubApi.deleteImport(imp.id),
    onSuccess: () => { refresh(); say('success', 'Shipment deleted.'); },
    onError: (e: any) => oops(e, "Couldn't delete the shipment."),
  });
  const addLot = useMutation({
    mutationFn: (lineId: string) => hubApi.addLot(imp.id, {
      importLineId: lineId,
      lotNumber: lotForm.lotNumber.trim(),
      productionDate: lotForm.productionDate || undefined,
      expirationDate: lotForm.expirationDate || undefined,
      location: lotForm.location.trim() || undefined,
      quantityRemaining: parseFloat(lotForm.quantityRemaining) || 0,
    }),
    onSuccess: () => { refresh(); setLotFormFor(null); setLotForm({ lotNumber: '', productionDate: '', expirationDate: '', location: '', quantityRemaining: '' }); },
    onError: (e: any) => oops(e, "Couldn't save the lot."),
  });
  const deleteLot = useMutation({
    mutationFn: (lotId: string) => hubApi.deleteLot(imp.id, lotId),
    onSuccess: refresh,
    onError: (e: any) => oops(e, "Couldn't remove the lot."),
  });
  const deleteLine = useMutation({
    mutationFn: (lineId: string) => hubApi.deleteImportLine(imp.id, lineId),
    onSuccess: refresh,
    onError: (e: any) => oops(e, "Couldn't remove the line."),
  });
  const addLine = useMutation({
    mutationFn: () => hubApi.addImportLine(imp.id, {
      productName: newLine.productName.trim(), sku: newLine.sku.trim() || undefined,
      quantity: parseFloat(newLine.quantity) || 0, unit: newLine.unit.trim() || 'unit',
      unitCostCents: toCents(newLine.unitCost),
    }),
    onSuccess: () => { refresh(); setNewLine(null); },
    onError: (e: any) => oops(e, "Couldn't add the line."),
  });
  // Hapag-Lloyd: with a platform key the ETA/status refresh from their live
  // events; without one the honest answer is their tracker, deep-linked below.
  const track = useMutation({
    mutationFn: () => hubApi.trackImport(imp.id),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      if (d.tracked) {
        refresh();
        say('success', d.arrived
          ? `Hapag-Lloyd says this container has arrived${d.arrivedAt ? ` (${fmtDate(d.arrivedAt)})` : ''}.`
          : d.eta
            ? `ETA refreshed from Hapag-Lloyd: ${fmtDate(d.eta)}.`
            : 'Hapag-Lloyd answered, but no arrival estimate is published for this container yet.');
      } else {
        say('error', "Live tracking isn't configured on the platform yet — the Hapag-Lloyd link on this shipment opens their tracker with the container pre-filled.");
      }
    },
    onError: (e: any) => oops(e, "Couldn't refresh from Hapag-Lloyd."),
  });

  const saveDocs = (documents: any[]) => update.mutate({ documents });

  return (
    <div className="card">
      <button onClick={onToggle} className="w-full p-4 flex items-center justify-between gap-3 text-left">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-900 truncate">{imp.supplierName}</p>
            {imp.reference && <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{imp.reference}</span>}
            <span className={statusChip(imp.status)}>{STATUS_LABEL[imp.status] || imp.status}</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0 bg-slate-100 text-slate-600" title={imp.entity === 'eu' ? "Lands in the EU entity's books" : "Lands in the US entity's books"}>{String(imp.entity || 'us').toUpperCase()}</span>
          </div>
          <p className="text-sm text-slate-500 truncate">
            {imp.shipDate ? `Shipped ${fmtDate(imp.shipDate)}` : 'No ship date'}
            {imp.eta ? ` · ETA ${fmtDate(imp.eta)}` : ''}
            {imp.arrivedAt ? ` · Arrived ${fmtDate(imp.arrivedAt)}` : ''}
            {' '}· {(imp.lines || []).length} line{(imp.lines || []).length === 1 ? '' : 's'}
            {invoiceTotal > 0 ? ` · invoice ${fmt(invoiceTotal)} → delivered ${fmt(deliveredTotal)}` : ''}
          </p>
        </div>
        <ChevronDown className={clsx('w-4 h-4 text-slate-400 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Container tracking — always visible on the row when there's a reference */}
      {imp.reference && (
        <div className="px-4 pb-3 -mt-1 flex flex-wrap items-center gap-3">
          <a
            href={`https://www.hapag-lloyd.com/en/online-business/track/track-by-container-solution.html?container=${encodeURIComponent(imp.reference)}`}
            target="_blank" rel="noreferrer"
            className="text-xs text-primary-600 font-medium flex items-center gap-1">
            Track {imp.reference} on Hapag-Lloyd <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
          <button onClick={() => track.mutate()} disabled={track.isPending} className="btn btn-sm btn-secondary">
            {track.isPending ? 'Checking…' : 'Refresh ETA'}
          </button>
        </div>
      )}

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-3">
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {imp.status === 'in_transit' && (
              <button onClick={() => update.mutate({ status: 'arrived' })} disabled={update.isPending} className="btn btn-secondary text-sm">Mark arrived</button>
            )}
            {imp.status === 'arrived' && (
              <button onClick={() => update.mutate({ status: 'cleared' })} disabled={update.isPending} className="btn btn-secondary text-sm">Mark cleared</button>
            )}
            <button onClick={() => { setHf({
              supplierName: imp.supplierName || '', reference: imp.reference || '',
              shipDate: imp.shipDate ? String(imp.shipDate).slice(0, 10) : '',
              eta: imp.eta ? String(imp.eta).slice(0, 10) : '',
              arrivedAt: imp.arrivedAt ? String(imp.arrivedAt).slice(0, 10) : '',
              freight: imp.freightCents ? String(imp.freightCents / 100) : '',
              tariffPercent: imp.tariffPercent == null ? '' : String(parseFloat(imp.tariffPercent)),
              distributionPercent: imp.distributionPercent == null ? '' : String(parseFloat(imp.distributionPercent)),
              notes: imp.notes || '',
            }); setEditHeader(true); }} className="btn btn-ghost text-sm flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit details</button>
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} className="btn btn-ghost text-sm text-slate-400 hover:text-red-500 ml-auto">Delete</button>
            ) : (
              <span className="ml-auto flex items-center gap-2 text-sm text-slate-600">
                Delete this shipment and its lots?
                <button onClick={() => remove.mutate()} disabled={remove.isPending} className="btn btn-danger text-sm">{remove.isPending ? 'Deleting…' : 'Yes, delete'}</button>
                <button onClick={() => setConfirmDelete(false)} className="btn btn-ghost text-sm">Keep</button>
              </span>
            )}
          </div>

          {editHeader && hf && (
            <div className="card bg-slate-50 p-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={hf.supplierName} onChange={(e) => setHf({ ...hf, supplierName: e.target.value })} placeholder="Supplier *" className="input" />
                <input value={hf.reference} onChange={(e) => setHf({ ...hf, reference: e.target.value })} placeholder="Reference" className="input" />
                <label className="text-sm text-slate-600">Ship date<input type="date" value={hf.shipDate} onChange={(e) => setHf({ ...hf, shipDate: e.target.value })} className="input mt-1" /></label>
                <label className="text-sm text-slate-600">ETA<input type="date" value={hf.eta} onChange={(e) => setHf({ ...hf, eta: e.target.value })} className="input mt-1" /></label>
                <label className="text-sm text-slate-600">Arrived<input type="date" value={hf.arrivedAt} onChange={(e) => setHf({ ...hf, arrivedAt: e.target.value })} className="input mt-1" /></label>
                <label className="text-sm text-slate-600">Freight ($)<input type="number" min="0" step="0.01" value={hf.freight} onChange={(e) => setHf({ ...hf, freight: e.target.value })} className="input mt-1" /></label>
                <label className="text-sm text-slate-600">Tariff % (blank = account default)
                  <input type="number" min="0" step="0.1" value={hf.tariffPercent} onChange={(e) => setHf({ ...hf, tariffPercent: e.target.value })} className="input mt-1" /></label>
                <label className="text-sm text-slate-600">Distribution % (blank = account default)
                  <input type="number" min="0" step="0.1" value={hf.distributionPercent} onChange={(e) => setHf({ ...hf, distributionPercent: e.target.value })} className="input mt-1" /></label>
              </div>
              <textarea value={hf.notes} onChange={(e) => setHf({ ...hf, notes: e.target.value })} placeholder="Notes" className="input w-full min-h-[52px]" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditHeader(false)} className="btn btn-ghost">Cancel</button>
                <button onClick={() => update.mutate({
                  supplierName: hf.supplierName.trim(), reference: hf.reference.trim(),
                  shipDate: hf.shipDate || null, eta: hf.eta || null, arrivedAt: hf.arrivedAt || null,
                  freightCents: toCents(hf.freight),
                  tariffPercent: hf.tariffPercent === '' ? null : parseFloat(hf.tariffPercent),
                  distributionPercent: hf.distributionPercent === '' ? null : parseFloat(hf.distributionPercent),
                  notes: hf.notes,
                })} disabled={!hf.supplierName.trim() || update.isPending} className="btn btn-primary">{update.isPending ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}

          {/* Lines + landed cost */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Qty</th>
                  <th className="py-2 pr-3">Invoice/unit</th>
                  <th className="py-2 pr-3">+ Freight</th>
                  <th className="py-2 pr-3">+ Tariff</th>
                  <th className="py-2 pr-3">+ Distribution</th>
                  <th className="py-2 pr-3">Delivered/unit</th>
                  <th className="py-2 pr-3" title="Derived from the size in the product name or SKU — liters for ml/L, per 100 g for oz. Blank when no size parses.">Per liter / 100 g</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {(imp.lines || []).map((l: any) => (
                  <tr key={l.id} className="border-t border-slate-100 align-top">
                    <td className="py-2 pr-3">
                      <p className="font-medium text-slate-900">{l.productName}</p>
                      <p className="text-xs text-slate-400">{[l.sku, l.menuItemId ? 'linked to catalog' : 'not in catalog'].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{parseFloat(l.quantity)} {l.unit}</td>
                    <td className="py-2 pr-3">{fmt(l.cost?.perUnit?.invoiceCents || 0)}</td>
                    <td className="py-2 pr-3 text-slate-500">{fmt(l.cost?.perUnit?.freightCents || 0)}</td>
                    <td className="py-2 pr-3 text-slate-500">{fmt(l.cost?.perUnit?.tariffCents || 0)}</td>
                    <td className="py-2 pr-3 text-slate-500">{fmt(l.cost?.perUnit?.distributionCents || 0)}</td>
                    <td className="py-2 pr-3 font-semibold text-slate-900">{fmt(l.cost?.perUnit?.deliveredCents || 0)}</td>
                    <td className="py-2 pr-3">{l.cost?.perLiterCents != null ? `${fmt(l.cost.perLiterCents)}/L` : l.cost?.per100gCents != null ? `${fmt(l.cost.per100gCents)}/100g` : '—'}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => deleteLine.mutate(l.id)} className="p-1 text-slate-300 hover:text-red-500" title="Remove line"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(imp.lines || []).length === 0 && <p className="text-sm text-slate-400 py-2">No lines yet — add what's on the commercial invoice.</p>}
          </div>
          <p className="text-xs text-slate-400">
            Landed cost = invoice → + freight (split by each line's share of the invoice) → + tariff {imp.tariffPercent != null ? `${parseFloat(imp.tariffPercent)}%` : `${defaults.tariffPercent}% (account default)`} → + distribution {imp.distributionPercent != null ? `${parseFloat(imp.distributionPercent)}%` : `${defaults.distributionPercent}% (account default)`}. Computed fresh from this shipment's entries.
          </p>

          {!newLine ? (
            <button onClick={() => setNewLine({ ...emptyLine })} className="btn btn-ghost text-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Add line</button>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto_auto] gap-2 items-center">
              <input value={newLine.productName} onChange={(e) => setNewLine({ ...newLine, productName: e.target.value })} placeholder="Product" className="input" />
              <input value={newLine.sku} onChange={(e) => setNewLine({ ...newLine, sku: e.target.value })} placeholder="SKU" className="input" />
              <input type="number" min="0" value={newLine.quantity} onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })} placeholder="Qty" className="input" />
              <input value={newLine.unit} onChange={(e) => setNewLine({ ...newLine, unit: e.target.value })} placeholder="Unit" className="input" />
              <input type="number" min="0" step="0.01" value={newLine.unitCost} onChange={(e) => setNewLine({ ...newLine, unitCost: e.target.value })} placeholder="Cost/unit $" className="input" />
              <button onClick={() => addLine.mutate()} disabled={!newLine.productName.trim() || !(parseFloat(newLine.quantity) > 0) || addLine.isPending} className="btn btn-primary text-sm">{addLine.isPending ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setNewLine(null)} className="btn btn-ghost text-sm">Cancel</button>
            </div>
          )}

          {/* Lots per line */}
          <div className="space-y-3">
            {(imp.lines || []).map((l: any) => (
              <div key={l.id} className="rounded-lg bg-slate-50 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Lots — {l.productName}</p>
                  <button onClick={() => { setLotFormFor(lotFormFor === l.id ? null : l.id); }} className="text-xs text-primary-600 font-medium">
                    {lotFormFor === l.id ? 'Close' : '+ Add lot'}
                  </button>
                </div>
                {(l.lots || []).length === 0 && lotFormFor !== l.id && (
                  <p className="text-xs text-slate-400">No lots recorded yet — add the lot number, dates and where it sits once received.</p>
                )}
                {(l.lots || []).map((lot: any) => <LotRow key={lot.id} lot={lot} unit={l.unit} onDelete={() => deleteLot.mutate(lot.id)} importId={imp.id} refresh={refresh} oops={oops} />)}
                {lotFormFor === l.id && (
                  <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 items-end">
                    <label className="text-xs text-slate-500">Lot number *<input value={lotForm.lotNumber} onChange={(e) => setLotForm({ ...lotForm, lotNumber: e.target.value })} className="input mt-1" /></label>
                    <label className="text-xs text-slate-500">Production<input type="date" value={lotForm.productionDate} onChange={(e) => setLotForm({ ...lotForm, productionDate: e.target.value })} className="input mt-1" /></label>
                    <label className="text-xs text-slate-500">Expiry<input type="date" value={lotForm.expirationDate} onChange={(e) => setLotForm({ ...lotForm, expirationDate: e.target.value })} className="input mt-1" /></label>
                    <label className="text-xs text-slate-500">Location<input value={lotForm.location} onChange={(e) => setLotForm({ ...lotForm, location: e.target.value })} placeholder="LA warehouse…" className="input mt-1" /></label>
                    <label className="text-xs text-slate-500">Qty remaining<input type="number" min="0" value={lotForm.quantityRemaining} onChange={(e) => setLotForm({ ...lotForm, quantityRemaining: e.target.value })} className="input mt-1" /></label>
                    <button onClick={() => addLot.mutate(l.id)} disabled={!lotForm.lotNumber.trim() || addLot.isPending} className="btn btn-primary text-sm">{addLot.isPending ? 'Saving…' : 'Save lot'}</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Documents (links, no uploads) */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documents</p>
            {(imp.documents || []).length === 0 && <p className="text-xs text-slate-400">Certificates, bills of lading, transaction certs — attach them by link so the certifier trail lives with the shipment.</p>}
            {(imp.documents || []).map((d: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <a href={d.url} target="_blank" rel="noreferrer" className="text-primary-600 font-medium flex items-center gap-1 truncate">
                  {d.name} <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
                <button onClick={() => saveDocs((imp.documents || []).filter((_: any, j: number) => j !== i))} className="p-1 text-slate-300 hover:text-red-500" title="Remove link"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 items-center">
              <input value={docForm.name} onChange={(e) => setDocForm({ ...docForm, name: e.target.value })} placeholder="Document name" className="input w-44" />
              <input value={docForm.url} onChange={(e) => setDocForm({ ...docForm, url: e.target.value })} placeholder="https://…" className="input flex-1 min-w-[180px]" />
              <button onClick={() => { if (docForm.url.trim()) { saveDocs([...(imp.documents || []), { name: docForm.name.trim() || docForm.url.trim(), url: docForm.url.trim() }]); setDocForm({ name: '', url: '' }); } }}
                disabled={!docForm.url.trim() || update.isPending} className="btn btn-secondary text-sm">Add link</button>
            </div>
          </div>

          {imp.notes && <p className="text-sm text-slate-500 whitespace-pre-wrap border-t border-slate-100 pt-3">{imp.notes}</p>}
        </div>
      )}
    </div>
  );
}

function LotRow({ lot, unit, onDelete, importId, refresh, oops }: {
  lot: any; unit: string; onDelete: () => void; importId: string; refresh: () => void; oops: (e: any, fallback: string) => void;
}) {
  const daysLeft = lot.expirationDate ? Math.ceil((new Date(lot.expirationDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : null;
  const updateQty = useMutation({
    mutationFn: (quantityRemaining: number) => hubApi.updateLot(importId, lot.id, { quantityRemaining }),
    onSuccess: refresh,
    onError: (e: any) => oops(e, "Couldn't update the lot."),
  });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="font-medium text-slate-900">Lot {lot.lotNumber}</span>
      {lot.productionDate && <span className="text-slate-500">made {fmtDate(lot.productionDate)}</span>}
      {lot.expirationDate && (
        daysLeft != null && daysLeft <= 0
          ? <span className="badge badge-danger">expired {fmtDate(lot.expirationDate)}</span>
          : daysLeft != null && daysLeft <= 60
            ? <span className="badge badge-warning">expires {fmtDate(lot.expirationDate)} · {daysLeft}d</span>
            : <span className="text-slate-500">expires {fmtDate(lot.expirationDate)}</span>
      )}
      {lot.location && <span className="text-slate-500">{lot.location}</span>}
      <span className="text-slate-600 flex items-center gap-1">
        <input type="number" min="0" defaultValue={parseFloat(lot.quantityRemaining)} key={lot.quantityRemaining}
          onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== parseFloat(lot.quantityRemaining)) updateQty.mutate(v); }}
          className="w-20 px-2 py-1 border border-slate-200 rounded-lg text-sm" />
        {unit} left
      </span>
      <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500" title="Remove lot"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ===========================================
// LANDED COST TREND
// ===========================================
function CostsTab({ defaults }: { defaults: any }) {
  const { data: trend = [], isPending } = useQuery({
    queryKey: ['hub-cost-trend'],
    queryFn: async () => (await hubApi.getCostTrend()).data?.data || [],
  });
  const STAGE_COLS: Array<[string, string]> = [
    ['invoiceCents', 'Invoice'], ['freightCents', 'Freight'], ['tariffCents', 'Tariff'],
    ['distributionCents', 'Distribution'], ['deliveredCents', 'Delivered'],
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary-600 shrink-0" />
        Each product across its shipments, per unit — computed from the invoice lines, freight, tariff ({defaults.tariffPercent}% default) and distribution ({defaults.distributionPercent}% default) you recorded. A badge means that stage moved more than 2.5% vs the shipment before.
      </p>
      {isPending ? (
        <div className="card p-8 text-center text-slate-400">Working it out…</div>
      ) : trend.length === 0 ? (
        <div className="card p-10 text-center text-slate-500">
          No cost history yet — record a shipment with line costs on the Shipments tab and the trend builds itself.
        </div>
      ) : (
        trend.map((p: any) => (
          <div key={p.key} className="card p-4 space-y-2">
            <p className="font-semibold text-slate-900">{p.productName}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                    <th className="py-2 pr-3">Shipment</th>
                    {STAGE_COLS.map(([k, label]) => <th key={k} className="py-2 pr-3">{label}/unit</th>)}
                    <th className="py-2 pr-3" title="Derived from the size in the product name or SKU — liters for ml/L, per 100 g for oz.">Per liter / 100 g</th>
                  </tr>
                </thead>
                <tbody>
                  {p.points.map((pt: any, i: number) => (
                    <tr key={`${pt.importId}-${i}`} className="border-t border-slate-100">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <span className="font-medium text-slate-900">{pt.reference || pt.supplierName}</span>
                        <span className="text-slate-400"> · {fmtDate(pt.date)}</span>
                      </td>
                      {STAGE_COLS.map(([k]) => (
                        <td key={k} className="py-2 pr-3 whitespace-nowrap">
                          {fmt(pt.perUnit?.[k] || 0)}
                          {pt.jumps?.[k] != null && (
                            <span className={clsx('ml-1 badge', pt.jumps[k] > 0 ? 'badge-danger' : 'badge-success')}>
                              {pt.jumps[k] > 0 ? '+' : ''}{pt.jumps[k]}%
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="py-2 pr-3">{pt.perLiterCents != null ? `${fmt(pt.perLiterCents)}/L` : pt.per100gCents != null ? `${fmt(pt.per100gCents)}/100g` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ===========================================
// ORGANIC MASS BALANCE
// ===========================================
function OrganicTab({ say, oops, refresh }: {
  say: (tone: 'success' | 'error', text: string) => void; oops: (e: any, fallback: string) => void; refresh: () => void;
}) {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({
    queryKey: ['hub-products'],
    queryFn: async () => (await hubApi.getProducts()).data?.data || [],
  });
  const { data: balance = [], isPending } = useQuery({
    queryKey: ['hub-mass-balance'],
    queryFn: async () => (await hubApi.getMassBalance()).data?.data || [],
  });
  const toggleOrganic = useMutation({
    mutationFn: ({ id, organic }: { id: string; organic: boolean }) => hubApi.setProductOrganic(id, organic),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hub-products'] }); qc.invalidateQueries({ queryKey: ['hub-mass-balance'] }); },
    onError: (e: any) => oops(e, "Couldn't update the product."),
  });
  const saveLine = useMutation({
    mutationFn: ({ importId, lineId, data }: { importId: string; lineId: string; data: Record<string, unknown> }) =>
      hubApi.updateImportLine(importId, lineId, data),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ['hub-mass-balance'] }); },
    onError: (e: any) => oops(e, "Couldn't save the quantity."),
  });

  const exportCsv = (p: any) => {
    const head = ['Product', 'Shipment', 'Supplier', 'Date', 'Received', 'Sold', 'Losses', 'Remaining', 'Unit', 'Documents'];
    const rows = p.rows.map((r: any) => [
      p.name, r.reference || '', r.supplierName || '', r.date ? new Date(r.date).toISOString().slice(0, 10) : '',
      r.receivedQty, r.soldQty, r.lossQty, r.remainingQty, r.unit,
      (r.documents || []).map((d: any) => d.url).join(' '),
    ]);
    rows.push([p.name, 'TOTAL', '', '', p.totals.receivedQty, p.totals.soldQty, p.totals.lossQty, p.totals.remainingQty, '', '']);
    const csv = [head, ...rows].map((r: any[]) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mass-balance-${p.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    say('success', `Mass balance for ${p.name} exported.`);
  };

  const organicCount = products.filter((p: any) => p.organic).length;

  return (
    <div className="space-y-4">
      {/* Flag products */}
      <div className="card p-4 space-y-3">
        <div>
          <p className="font-medium text-slate-900 flex items-center gap-2"><Leaf className="w-4 h-4 text-emerald-600" /> Organic products</p>
          <p className="text-xs text-slate-500 mt-0.5">Flag the products your certifier tracks — the flag lives on the product in your catalog, so it follows it everywhere.</p>
        </div>
        {products.length === 0 ? (
          <p className="text-sm text-slate-400">No products in your catalog yet — they appear here once your sell-side catalog has items.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {products.map((p: any) => (
              <button key={p.id} onClick={() => toggleOrganic.mutate({ id: p.id, organic: !p.organic })} disabled={toggleOrganic.isPending}
                className={clsx('px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                  p.organic ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}>
                {p.organic ? '✓ ' : ''}{p.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {isPending ? (
        <div className="card p-8 text-center text-slate-400">Building the balance…</div>
      ) : organicCount === 0 ? (
        <div className="card p-10 text-center text-slate-500">
          Flag a product organic above and its shipments line up here as the received / sold / losses / remaining sheet your certifier asks for.
        </div>
      ) : (
        balance.map((p: any) => (
          <div key={p.menuItemId} className="card p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">
                  Received from shipments on this page. For context, wholesale orders show {p.soldFromOrders} unit{p.soldFromOrders === 1 ? '' : 's'} sold of this product overall.
                </p>
              </div>
              <button onClick={() => exportCsv(p)} className="btn btn-secondary text-sm flex items-center gap-1" disabled={p.rows.length === 0}>
                <Download className="w-3.5 h-3.5" /> Export for certifier
              </button>
            </div>
            {p.rows.length === 0 ? (
              <p className="text-sm text-slate-400">No shipments of this product yet — record one on the Shipments tab (the product name has to match the catalog, or link the line to it).</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                        <th className="py-2 pr-3">Shipment</th>
                        <th className="py-2 pr-3">Received</th>
                        <th className="py-2 pr-3">Sold</th>
                        <th className="py-2 pr-3">Losses</th>
                        <th className="py-2 pr-3">Remaining</th>
                        <th className="py-2 pr-3">Documents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.rows.map((r: any) => (
                        <tr key={r.lineId} className="border-t border-slate-100">
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <span className="font-medium text-slate-900">{r.reference || r.supplierName}</span>
                            <span className="text-slate-400"> · {fmtDate(r.date)}</span>
                          </td>
                          <td className="py-2 pr-3">{r.receivedQty} {r.unit}</td>
                          <td className="py-2 pr-3">
                            <input type="number" min="0" defaultValue={r.soldQty} key={`s-${r.lineId}-${r.soldQty}`}
                              onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== r.soldQty) saveLine.mutate({ importId: r.importId, lineId: r.lineId, data: { soldQty: v } }); }}
                              className="w-24 px-2 py-1 border border-slate-200 rounded-lg text-sm" />
                          </td>
                          <td className="py-2 pr-3">
                            <input type="number" min="0" defaultValue={r.lossQty} key={`l-${r.lineId}-${r.lossQty}`}
                              onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== r.lossQty) saveLine.mutate({ importId: r.importId, lineId: r.lineId, data: { lossQty: v } }); }}
                              className="w-24 px-2 py-1 border border-slate-200 rounded-lg text-sm" />
                          </td>
                          <td className={clsx('py-2 pr-3 font-semibold', r.remainingQty < 0 ? 'text-red-600' : 'text-slate-900')}>{r.remainingQty} {r.unit}</td>
                          <td className="py-2 pr-3">
                            {(r.documents || []).length === 0 ? <span className="text-slate-300">—</span> : (r.documents || []).map((d: any, i: number) => (
                              <a key={i} href={d.url} target="_blank" rel="noreferrer" className="text-primary-600 text-xs font-medium mr-2">{d.name}</a>
                            ))}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-slate-200 font-semibold text-slate-900">
                        <td className="py-2 pr-3">Total</td>
                        <td className="py-2 pr-3">{p.totals.receivedQty}</td>
                        <td className="py-2 pr-3">{p.totals.soldQty}</td>
                        <td className="py-2 pr-3">{p.totals.lossQty}</td>
                        <td className={clsx('py-2 pr-3', p.totals.remainingQty < 0 && 'text-red-600')}>{p.totals.remainingQty}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400">
                  Sold and losses are recorded by hand per shipment — orders reference the product, not the shipment, so Mise won't guess the split. Edits save when you leave the field.
                </p>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
